import { useLayoutEffect, useMemo, useRef, useState, type ComponentPropsWithoutRef } from "react";

import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { cn } from "@/shared/lib/utils";

import {
  amountInputFormat,
  caretAfterDigits,
  digitsBefore,
  maskAmountInput,
  type MaskedAmount,
} from "../../utils/cryptoSwapAmountInput";
import { canonicalAmount } from "../../utils/cryptoSwapAmounts";
import { formatAmount } from "../../utils/cryptoSwapFormat";

export interface ConverterAmountInputProps extends Omit<
  ComponentPropsWithoutRef<"input">,
  "value" | "onChange" | "type" | "inputMode"
> {
  /** Plain decimal string, the same value that reaches the wire. */
  value: string;
  onValueChange: (value: string) => void;
  decimals: number;
  locale: string;
  /** Applied to values set from outside (a quote); what the user types is never padded. */
  minFractionDigits?: number;
}

/** Groups thousands as the user types ("1.234.567" in es) while the parent only ever sees the plain decimal. */
export function ConverterAmountInput({
  value,
  onValueChange,
  decimals,
  locale,
  minFractionDigits = 0,
  onBlur,
  className,
  ...rest
}: ConverterAmountInputProps) {
  const format = useMemo(() => amountInputFormat(locale, decimals), [locale, decimals]);
  const [draft, setDraft] = useState<MaskedAmount | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const caretDigitsRef = useRef<number | null>(null);

  const shown =
    draft && canonicalAmount(draft.value) === canonicalAmount(value)
      ? draft.display
      : value
        ? formatAmount(value, locale, { min: Math.min(minFractionDigits, decimals), max: decimals })
        : "";

  useLayoutEffect(() => {
    const input = inputRef.current;
    const digits = caretDigitsRef.current;
    if (!input || digits === null) return;
    caretDigitsRef.current = null;
    if (document.activeElement !== input) return;
    const position = caretAfterDigits(input.value, digits);
    input.setSelectionRange(position, position);
  });

  const handleChange = (raw: string) => {
    const masked = maskAmountInput(raw, format, shown);
    const caret = inputRef.current?.selectionStart ?? raw.length;
    caretDigitsRef.current = caret >= raw.length ? null : digitsBefore(raw, caret);
    setDraft(masked);
    // A keystroke the mask swallowed is not an edit: it must not take over as the anchor or re-price.
    if (canonicalAmount(masked.value) !== canonicalAmount(value)) onValueChange(masked.value);
  };

  return (
    <ColocaInput
      ref={inputRef}
      type="text"
      inputMode={decimals > 0 ? "decimal" : "numeric"}
      autoComplete="off"
      spellCheck={false}
      value={shown}
      onValueChange={handleChange}
      onBlur={(event) => {
        setDraft(null);
        onBlur?.(event);
      }}
      className={cn("font-mono tabular-nums", className)}
      {...rest}
    />
  );
}
